/**
 * BestOddsMarketFilter - Multi-select by marketKey for Market Anomalies.
 *
 * Options come from unique marketKeys in the current anomalies list
 * (plus any persisted selection). Labels via getMarginTypeName.
 * Semantics: null = all; string[] = only those markets (empty => none).
 */

import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useBestOdds } from '../context/BestOddsContext';
import { useTranslation } from '../../i18n/hooks';
import { getMarginTypeName } from '../../surebets/utils/surebetUtils';
import './BestOddsBookmakerFilter.css';

const BestOddsMarketFilter = ({ className = '' }) => {
  const { t, language } = useTranslation();
  const {
    anomalies,
    filters,
    setSelectedMarketKeys
  } = useBestOdds();

  const availableMarkets = useMemo(() => {
    const keys = new Set();

    (anomalies || []).forEach((item) => {
      const key = item?.marketKey;
      if (key && String(key).trim()) {
        keys.add(String(key));
      }
    });

    if (Array.isArray(filters?.selectedMarketKeys)) {
      filters.selectedMarketKeys.forEach((key) => {
        if (key && String(key).trim()) {
          keys.add(String(key));
        }
      });
    }

    return Array.from(keys)
      .map((key) => ({
        key,
        label: getMarginTypeName(key, language)
      }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [anomalies, filters?.selectedMarketKeys, language]);

  const availableKeys = useMemo(
    () => availableMarkets.map((item) => item.key),
    [availableMarkets]
  );

  const selectedMarketKeys = filters?.selectedMarketKeys;
  const isAllSelected = selectedMarketKeys === null;

  const selectedCount = isAllSelected
    ? availableKeys.length
    : (Array.isArray(selectedMarketKeys) ? selectedMarketKeys.length : 0);

  const isMarketSelected = useCallback((marketKey) => {
    if (selectedMarketKeys === null) {
      return true;
    }
    if (!Array.isArray(selectedMarketKeys)) {
      return true;
    }
    return selectedMarketKeys.includes(marketKey);
  }, [selectedMarketKeys]);

  const handleSelectAll = useCallback(() => {
    setSelectedMarketKeys(null);
  }, [setSelectedMarketKeys]);

  const handleDeselectAll = useCallback(() => {
    setSelectedMarketKeys([]);
  }, [setSelectedMarketKeys]);

  const handleToggle = useCallback((marketKey) => {
    if (selectedMarketKeys === null || !Array.isArray(selectedMarketKeys)) {
      setSelectedMarketKeys(availableKeys.filter((key) => key !== marketKey));
      return;
    }

    if (selectedMarketKeys.includes(marketKey)) {
      setSelectedMarketKeys(selectedMarketKeys.filter((key) => key !== marketKey));
      return;
    }

    const next = [...selectedMarketKeys, marketKey];
    const allSelected = availableKeys.length > 0
      && availableKeys.every((key) => next.includes(key));

    setSelectedMarketKeys(allSelected ? null : next);
  }, [availableKeys, selectedMarketKeys, setSelectedMarketKeys]);

  if (availableMarkets.length === 0) {
    return null;
  }

  return (
    <div
      className={`best-odds-bookmaker-filter best-odds-bookmaker-filter--wide ${className}`.trim()}
      role="group"
      aria-label={t('bestOdds.filters.marketsAriaLabel')}
    >
      <div className="best-odds-bookmaker-filter__header">
        <span className="best-odds-bookmaker-filter__label">
          {t('bestOdds.filters.marketsLabel')}
          <span className="best-odds-bookmaker-filter__count">
            ({selectedCount}/{availableKeys.length})
          </span>
        </span>
        <div className="best-odds-bookmaker-filter__actions">
          <button
            type="button"
            className="best-odds-bookmaker-filter__action-btn"
            onClick={handleSelectAll}
            disabled={isAllSelected}
          >
            {t('bestOdds.filters.marketsSelectAll')}
          </button>
          <button
            type="button"
            className="best-odds-bookmaker-filter__action-btn"
            onClick={handleDeselectAll}
            disabled={Array.isArray(selectedMarketKeys) && selectedMarketKeys.length === 0}
          >
            {t('bestOdds.filters.marketsDeselectAll')}
          </button>
        </div>
      </div>

      <div className="best-odds-bookmaker-filter__grid">
        {availableMarkets.map((market) => {
          const checked = isMarketSelected(market.key);
          return (
            <label
              key={market.key}
              className={`best-odds-bookmaker-filter__item${checked ? ' is-selected' : ''}`}
            >
              <input
                type="checkbox"
                className="best-odds-bookmaker-filter__checkbox"
                checked={checked}
                onChange={() => handleToggle(market.key)}
                value={market.key}
              />
              <span className="best-odds-bookmaker-filter__name" title={market.label}>
                {market.label}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

BestOddsMarketFilter.propTypes = {
  className: PropTypes.string
};

export default BestOddsMarketFilter;
